import React from 'react';
import { Box, Paper, Typography, useTheme } from '@mui/material';
import { useHelpTypography } from '../../hooks/useHelpTypography';
import { helpTextRenderingSx, HELP_CONTENT_TITLE_SX, HELP_CONTENT_LABEL_SX, HELP_CONTENT_BODY_SX, HELP_CONTENT_DESCRIPTION_SX, HELP_CONTENT_STEP_BADGE_SX } from '../../constants/help-typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

type CalloutVariant = 'info' | 'warning' | 'tip';

type GuideStep = {
  title: string;
  bullets?: string[];
  body?: string;
};

type ContentBlock =
  | { type: 'section'; title: string; body?: string; steps?: string[]; bullets?: string[] }
  | { type: 'guide'; steps: GuideStep[] }
  | { type: 'reference'; intro: string; items: string[] }
  | { type: 'steps'; steps: string[] }
  | { type: 'bullets'; intro?: string; items: string[] }
  | { type: 'callout'; variant: CalloutVariant; title?: string; body?: string; bullets?: string[] }
  | { type: 'paragraph'; text: string };

const getLines = (block: string) =>
  block
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

const CALLOUT_TITLE_PATTERN =
  /^(warning|important|best practice|ملاحظة مهمة|تحذير):?\s*(.*)$/i;

const stripStepPrefix = (line: string) => line.replace(/^\d+\.\s*/, '').trim();
const stripBulletPrefix = (line: string) => line.replace(/^•\s*/, '').trim();

const OPTIONS_SECTION_PATTERN = /^(options|available options|الخيارات|الخيارات المتاحة)$/i;

const parseLabeledItem = (item: string) => {
  const parts = item.split(/\s+[—–-]\s+/);
  if (parts.length >= 2) {
    return { label: parts[0].trim(), description: parts.slice(1).join(' — ').trim() };
  }
  return { label: item.trim(), description: undefined };
};

const getFillLevelPercent = (label: string): number | null => {
  const normalized = label.toLowerCase();

  if (/quarter|ربع/.test(normalized)) return 25;
  if (/half|نصف/.test(normalized) && !/three|ثلاثة/.test(normalized)) return 50;
  if (/three quarter|ثلاثة أرباع|three.quarter/i.test(normalized)) return 75;
  if (/^full\b|ممتلئة بالكامل|بالكامل/.test(normalized)) return 100;

  return null;
};

const isFillLevelBulletSet = (items: string[]) =>
  items.map((item) => getFillLevelPercent(parseLabeledItem(item).label)).filter((value) => value !== null)
    .length >= 3;

const isOptionsSection = (block: ContentBlock) =>
  block.type === 'section' && OPTIONS_SECTION_PATTERN.test(block.title.trim());

const hasLabeledBullets = (items: string[]) =>
  items.some((item) => /\s+[—–-]\s+/.test(item));

const isTipParagraph = (text: string) => /^(choose|اختر|tip:|نصيحة)/i.test(text.trim());

const parseGuideStepBlock = (block: string): GuideStep => {
  const lines = getLines(block);
  const headerText = lines[0].replace(/^\d+\.\s*/, '').trim();
  const title = headerText.replace(/:$/, '').trim();
  const subLines = lines.slice(1);
  const subBullets = subLines.filter((line) => line.startsWith('•')).map(stripBulletPrefix);

  if (subBullets.length > 0) {
    return { title, bullets: subBullets };
  }

  if (subLines.length > 0) {
    return { title, body: subLines.join('\n') };
  }

  return { title };
};

const getCalloutVariant = (title: string): CalloutVariant => {
  const normalized = title.toLowerCase();
  if (normalized.includes('warning') || normalized.includes('تحذير')) return 'warning';
  if (normalized.includes('best practice')) return 'tip';
  return 'info';
};

const parseContentBlock = (block: string): ContentBlock => {
  const lines = block
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return { type: 'paragraph', text: block };
  }

  const calloutMatch = lines[0].match(CALLOUT_TITLE_PATTERN);
  if (calloutMatch) {
    const title = calloutMatch[1];
    const inlineBody = calloutMatch[2]?.trim();
    const rest = lines.slice(1);
    const bullets = rest.filter((line) => line.startsWith('•')).map(stripBulletPrefix);
    const body =
      inlineBody ||
      (rest.length > 0 && bullets.length === 0 ? rest.join('\n') : undefined);

    return {
      type: 'callout',
      variant: getCalloutVariant(title),
      title,
      body,
      bullets: bullets.length > 0 ? bullets : undefined,
    };
  }

  if (lines.every((line) => line.startsWith('•'))) {
    return { type: 'bullets', items: lines.map(stripBulletPrefix) };
  }

  const bulletIndex = lines.findIndex((line) => line.startsWith('•'));
  if (bulletIndex > 0) {
    return {
      type: 'bullets',
      intro: lines.slice(0, bulletIndex).join('\n'),
      items: lines.slice(bulletIndex).map(stripBulletPrefix),
    };
  }

  if (lines[0].endsWith(':') && lines.length > 1) {
    const title = lines[0].slice(0, -1).trim();
    const rest = lines.slice(1);
    const numbered = rest.filter((line) => /^\d+\.\s/.test(line));
    const bullets = rest.filter((line) => line.startsWith('•')).map(stripBulletPrefix);

    if (numbered.length > 0 && numbered.length === rest.length) {
      return { type: 'section', title, steps: numbered.map(stripStepPrefix) };
    }

    if (bullets.length > 0) {
      const nonBullets = rest.filter((line) => !line.startsWith('•'));
      return {
        type: 'section',
        title,
        body: nonBullets.length > 0 ? nonBullets.join('\n') : undefined,
        bullets,
      };
    }

    if (rest.length === 1) {
      return { type: 'section', title, body: rest[0] };
    }

    return { type: 'section', title, body: rest.join('\n') };
  }

  if (lines.length === 1 && /^\d+\.\s/.test(lines[0])) {
    return { type: 'steps', steps: [stripStepPrefix(lines[0])] };
  }

  return { type: 'paragraph', text: block };
};

const parseArticleContent = (content: string): ContentBlock[] => {
  const rawBlocks = content
    .split(/\n\n+/)
    .map((block) => block.trim())
    .filter(Boolean);

  const blocks: ContentBlock[] = [];
  let index = 0;

  while (index < rawBlocks.length) {
    const block = rawBlocks[index];
    const lines = getLines(block);

    if (/^\d+\.\s/.test(lines[0] ?? '')) {
      const steps: GuideStep[] = [];
      while (index < rawBlocks.length) {
        const stepLines = getLines(rawBlocks[index]);
        if (!/^\d+\.\s/.test(stepLines[0] ?? '')) {
          break;
        }
        steps.push(parseGuideStepBlock(rawBlocks[index]));
        index += 1;
      }
      blocks.push({ type: 'guide', steps });
      continue;
    }

    if (index + 1 < rawBlocks.length) {
      const nextLines = getLines(rawBlocks[index + 1]);
      if (lines.length > 0 && !lines[0].startsWith('•') && nextLines.every((line) => line.startsWith('•'))) {
        const items = nextLines.map(stripBulletPrefix);
        if (hasLabeledBullets(items)) {
          blocks.push({ type: 'reference', intro: block, items });
          index += 2;
          continue;
        }
      }
    }

    const bulletIndex = lines.findIndex((line) => line.startsWith('•'));
    if (bulletIndex > 0) {
      const items = lines.slice(bulletIndex).map(stripBulletPrefix);
      if (hasLabeledBullets(items)) {
        blocks.push({
          type: 'reference',
          intro: lines.slice(0, bulletIndex).join('\n'),
          items,
        });
        index += 1;
        continue;
      }
    }

    if (lines.length === 1 && /^\d+\.\s/.test(lines[0])) {
      const steps: string[] = [];
      while (index < rawBlocks.length) {
        const stepLines = getLines(rawBlocks[index]);
        if (stepLines.length === 1 && /^\d+\.\s/.test(stepLines[0])) {
          steps.push(stripStepPrefix(stepLines[0]));
          index += 1;
        } else {
          break;
        }
      }
      blocks.push({ type: 'steps', steps });
      continue;
    }

    blocks.push(parseContentBlock(block));
    index += 1;
  }

  return blocks;
};

function SubBulletList({ items }: { items: string[] }) {
  const theme = useTheme();

  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        mt: 1.25,
        p: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      {items.map((item) => {
        const { label, description } = parseLabeledItem(item);

        return (
          <Box
            component="li"
            key={item}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              pl: 1.5,
              borderLeft: `2px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ ...HELP_CONTENT_LABEL_SX, color: 'text.primary' }}>
                {label}
              </Typography>
              {description ? (
                <Typography sx={{ ...HELP_CONTENT_DESCRIPTION_SX, mt: 0.25, color: 'text.secondary' }}>
                  {description}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function GuideStepList({ steps }: { steps: GuideStep[] }) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {steps.map((step, stepIndex) => (
        <Paper
          key={`${stepIndex}-${step.title}`}
          elevation={0}
          sx={{
            p: 2,
            borderRadius: '14px',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
            boxShadow: theme.shadows[1],
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${theme.palette.primary.main}18`,
                color: 'primary.main',
                ...HELP_CONTENT_STEP_BADGE_SX,
              }}
            >
              {stepIndex + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ ...HELP_CONTENT_TITLE_SX, color: 'text.primary' }}>
                {step.title}
              </Typography>
              {step.body ? (
                <Typography
                  sx={{ ...HELP_CONTENT_BODY_SX, mt: 0.75, color: 'text.primary', whiteSpace: 'pre-line' }}
                >
                  {step.body}
                </Typography>
              ) : null}
              {step.bullets?.length ? <SubBulletList items={step.bullets} /> : null}
            </Box>
          </Box>
        </Paper>
      ))}
    </Box>
  );
}

function StatusReferenceCard({ intro, items }: { intro: string; items: string[] }) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <CalloutBox variant="info" body={intro} />
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '16px',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
          boxShadow: theme.shadows[1],
        }}
      >
        <LabeledOptionList items={items} grid />
      </Paper>
    </Box>
  );
}

function StepList({ steps }: { steps: string[] }) {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {steps.map((step, stepIndex) => (
        <Box
          key={`${stepIndex}-${step.slice(0, 24)}`}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 1.5,
            borderRadius: '12px',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#F8FAFC',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${theme.palette.primary.main}18`,
              color: 'primary.main',
              ...HELP_CONTENT_STEP_BADGE_SX,
            }}
          >
            {stepIndex + 1}
          </Box>
          <Typography
            sx={{ ...HELP_CONTENT_BODY_SX, color: 'text.primary', pt: 0.25, whiteSpace: 'pre-line' }}
          >
            {step}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function FillLevelIndicator({ percent }: { percent: number }) {
  const theme = useTheme();

  return (
    <Box
      aria-hidden
      sx={{
        width: 44,
        height: 56,
        borderRadius: '10px',
        border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : '#CBD5E1'}`,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#F8FAFC',
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: `${percent}%`,
          bgcolor: 'primary.main',
          opacity: theme.palette.mode === 'dark' ? 0.9 : 0.82,
          transition: 'height 0.2s ease',
        }}
      />
    </Box>
  );
}

function FillLevelOptionList({ items }: { items: string[] }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 1.25,
      }}
    >
      {items.map((item) => {
        const { label, description } = parseLabeledItem(item);
        const percent = getFillLevelPercent(label) ?? 0;

        return (
          <Box
            key={item}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.75,
              borderRadius: '14px',
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#F8FAFC',
            }}
          >
            <FillLevelIndicator percent={percent} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ ...HELP_CONTENT_LABEL_SX, color: 'text.primary' }}>
                {label}
              </Typography>
              {description ? (
                <Typography sx={{ ...HELP_CONTENT_DESCRIPTION_SX, mt: 0.35, color: 'text.secondary' }}>
                  {description}
                </Typography>
              ) : (
                <Typography sx={{ ...HELP_CONTENT_DESCRIPTION_SX, mt: 0.35, color: 'text.secondary' }}>
                  {percent}%
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function LabeledOptionList({ items, grid = false }: { items: string[]; grid?: boolean }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: grid ? { xs: '1fr', sm: '1fr 1fr' } : '1fr',
        gap: 1.25,
      }}
    >
      {items.map((item) => {
        const { label, description } = parseLabeledItem(item);

        return (
          <Box
            key={item}
            sx={{
              p: 1.5,
              borderRadius: '12px',
              border: `1px solid ${theme.palette.divider}`,
              borderInlineStart: `3px solid ${theme.palette.primary.main}`,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#F8FAFC',
            }}
          >
            <Typography sx={{ ...HELP_CONTENT_LABEL_SX, color: 'text.primary' }}>
              {label}
            </Typography>
            {description ? (
              <Typography sx={{ ...HELP_CONTENT_DESCRIPTION_SX, mt: 0.35, color: 'text.secondary' }}>
                {description}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}

function BulletList({ intro, items }: { intro?: string; items: string[] }) {
  const useFillLevel = isFillLevelBulletSet(items);
  const useLabeled = !useFillLevel && hasLabeledBullets(items);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {intro ? (
        <Typography sx={{ ...HELP_CONTENT_BODY_SX, color: 'text.primary', whiteSpace: 'pre-line' }}>
          {intro}
        </Typography>
      ) : null}
      {useFillLevel ? (
        <FillLevelOptionList items={items} />
      ) : useLabeled ? (
        <LabeledOptionList items={items} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item) => (
            <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  flexShrink: 0,
                  mt: '0.72em',
                }}
              />
              <Typography sx={{ ...HELP_CONTENT_BODY_SX, color: 'text.primary', flex: 1 }}>
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function CalloutBox({
  variant,
  title,
  body,
  bullets,
}: {
  variant: CalloutVariant;
  title?: string;
  body?: string;
  bullets?: string[];
}) {
  const theme = useTheme();

  const palette = {
    info: {
      bg: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.12)' : '#EFF6FF',
      border: theme.palette.mode === 'dark' ? 'rgba(96, 165, 250, 0.35)' : '#BFDBFE',
      color: theme.palette.mode === 'dark' ? '#93C5FD' : '#1D4ED8',
      Icon: InfoOutlinedIcon,
    },
    warning: {
      bg: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.12)' : '#FFFBEB',
      border: theme.palette.mode === 'dark' ? 'rgba(251, 191, 36, 0.35)' : '#FDE68A',
      color: theme.palette.mode === 'dark' ? '#FCD34D' : '#B45309',
      Icon: WarningAmberOutlinedIcon,
    },
    tip: {
      bg: theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.1)' : '#F0FDF4',
      border: theme.palette.mode === 'dark' ? 'rgba(74, 222, 128, 0.3)' : '#BBF7D0',
      color: theme.palette.mode === 'dark' ? '#86EFAC' : '#15803D',
      Icon: LightbulbOutlinedIcon,
    },
  }[variant];

  const Icon = palette.Icon;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '14px',
        bgcolor: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Icon sx={{ fontSize: 20, color: palette.color, mt: 0.15, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {title ? (
            <Typography sx={{ ...HELP_CONTENT_LABEL_SX, color: palette.color, mb: body || bullets?.length ? 1 : 0 }}>
              {title}
            </Typography>
          ) : null}
          {body ? (
            <Typography sx={{ ...HELP_CONTENT_BODY_SX, color: 'text.primary', whiteSpace: 'pre-line' }}>
              {body}
            </Typography>
          ) : null}
          {bullets?.length ? (
            <Box sx={{ mt: body ? 1.25 : 0 }}>
              <BulletList items={bullets} />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

function SectionCard({
  title,
  body,
  steps,
  bullets,
}: {
  title: string;
  body?: string;
  steps?: string[];
  bullets?: string[];
}) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: '16px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
        boxShadow: theme.shadows[1],
      }}
    >
      {title ? (
        <Typography
          sx={{
            ...HELP_CONTENT_TITLE_SX,
            color: 'text.primary',
            mb: body || steps?.length || bullets?.length ? 1.75 : 0,
            pb: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {title}
        </Typography>
      ) : null}
      {body ? (
        <Typography
          sx={{ ...HELP_CONTENT_BODY_SX, color: 'text.primary', mb: steps?.length || bullets?.length ? 2 : 0, whiteSpace: 'pre-line' }}
        >
          {body}
        </Typography>
      ) : null}
      {steps?.length ? <StepList steps={steps} /> : null}
      {bullets?.length ? (
        <Box sx={{ mt: steps?.length ? 2 : 0 }}>
          {isFillLevelBulletSet(bullets) ? (
            <FillLevelOptionList items={bullets} />
          ) : hasLabeledBullets(bullets) ? (
            <LabeledOptionList items={bullets} />
          ) : (
            <BulletList items={bullets} />
          )}
        </Box>
      ) : null}
    </Paper>
  );
}

interface ArticleDetailContentProps {
  content: string;
}

const ArticleDetailContent: React.FC<ArticleDetailContentProps> = ({ content }) => {
  const { fontFamily, bodySx } = useHelpTypography();
  const blocks = parseArticleContent(content);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        maxWidth: 880,
        width: '100%',
        fontFamily,
        ...helpTextRenderingSx,
      }}
    >
      {blocks.map((block, index) => {
        const nextBlock = blocks[index + 1];
        const isLastBlock = index === blocks.length - 1;

        switch (block.type) {
          case 'guide':
            return <GuideStepList key={`guide-${index}`} steps={block.steps} />;
          case 'reference':
            return (
              <StatusReferenceCard
                key={`reference-${index}`}
                intro={block.intro}
                items={block.items}
              />
            );
          case 'section':
            return (
              <SectionCard
                key={`section-${index}`}
                title={block.title}
                body={block.body}
                steps={block.steps}
                bullets={block.bullets}
              />
            );
          case 'steps':
            return (
              <SectionCard key={`steps-${index}`} title="" steps={block.steps} />
            );
          case 'bullets':
            return (
              <Paper
                key={`bullets-${index}`}
                elevation={0}
                sx={(theme) => ({
                  p: 2.5,
                  borderRadius: '16px',
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : theme.palette.background.paper,
                  boxShadow: theme.shadows[1],
                })}
              >
                <BulletList intro={block.intro} items={block.items} />
              </Paper>
            );
          case 'callout':
            return (
              <CalloutBox
                key={`callout-${index}`}
                variant={block.variant}
                title={block.title}
                body={block.body}
                bullets={block.bullets}
              />
            );
          case 'paragraph':
          default:
            if (nextBlock && isOptionsSection(nextBlock)) {
              return (
                <CalloutBox key={`paragraph-${index}`} variant="info" body={block.text} />
              );
            }

            if (isLastBlock && blocks.length > 1 && isTipParagraph(block.text)) {
              return (
                <CalloutBox key={`paragraph-${index}`} variant="tip" body={block.text} />
              );
            }

            if (nextBlock?.type === 'bullets' && hasLabeledBullets(nextBlock.items)) {
              return (
                <CalloutBox key={`paragraph-${index}`} variant="info" body={block.text} />
              );
            }

            return (
              <Typography
                key={`paragraph-${index}`}
                sx={{
                  ...bodySx,
                  lineHeight: 1.8,
                  color: 'text.primary',
                  whiteSpace: 'pre-line',
                }}
              >
                {block.text}
              </Typography>
            );
        }
      })}
    </Box>
  );
};

export default ArticleDetailContent;
